import { useMemo } from 'react';
import { FAST_USER_INTERACTION_DEBOUNCE } from '../../../../../constants';
import { useDebouncedValue } from '../../../../../hooks/useDebounceValue';
import { useHannibalQuery } from '../../../../../schema/hannibal/HannibalContext';
import { SpecialtySuggestionsDocument } from '../../../../../schema/hannibal/types.generated';
import { EMPTY_LIST } from '../../../../../util/EMPTY_LIST';
import { SuggestionData } from '../../types';
import { SuggestionList } from '../SuggestionList/SuggestionList';
import {
  CategoryView,
  CategoryLabel,
  ListContainer,
} from '../SuggestionsLayoutTemplate/SuggestionsLayoutTemplate.styles';
import { TabProps } from '../TabProps';

interface Specialty {
  specialist: string;
}

export const mapSpecialtySuggestionsToSuggestionData = (suggestions: readonly Specialty[]) =>
  suggestions.flatMap<SuggestionData>(it => {
    const specialist = it.specialist;

    return [
      {
        type: 'FilterSuggestion',
        id: `specialty-${specialist}`,
        filters: { topic: { specialist: specialist } },
      },
    ];
  });

export const SpecialtySuggestions = ({
  searchTerm,
  onChoose,
  trackingData,
  toggleSingleSuggestionView,
  showSingleView,
  onFocus,
  listIndex = 0,
}: TabProps) => {
  const query = useDebouncedValue(searchTerm, FAST_USER_INTERACTION_DEBOUNCE);
  const { data } = useHannibalQuery(SpecialtySuggestionsDocument, { variables: { query } });

  const specialties = data?.searchSpecialtiesByName.specialties ?? EMPTY_LIST;

  const specialtySuggestions: SuggestionData[] = useMemo(() => {
    if (showSingleView) {
      return mapSpecialtySuggestionsToSuggestionData(specialties);
    }

    return mapSpecialtySuggestionsToSuggestionData(specialties).slice(0, 3);
  }, [showSingleView, specialties]);

  if (specialtySuggestions.length === 0) {
    return null;
  }

  const suggestions: SuggestionData[] = specialtySuggestions;

  return (
    <CategoryView>
      {!showSingleView && <CategoryLabel>Services</CategoryLabel>}
      <ListContainer onFocus={() => onFocus?.(listIndex)}>
        <SuggestionList
          suggestions={suggestions}
          onChoose={onChoose}
          trackingData={trackingData}
          searchTerm={searchTerm}
          toggleSingleSuggestionView={toggleSingleSuggestionView}
          showSingleView={showSingleView}
          suggestionType="Specialties"
          indexIdentifier={listIndex}
        />
      </ListContainer>
    </CategoryView>
  );
};
